import { t } from "@lingui/macro";
import React from "react";

import { PlaceIcon } from "@whoosh/design-system/src/components/icons_v2/general/icons/PlaceIcon";
import {
  Box,
  Text,
  HStack,
  Pressable,
} from "@whoosh/design-system/src/components/primitive";
import { Theme } from "@whoosh/design-system/src/theme/restyle/theme";

import RotatingGradientBorder from "./RotatingGradientBorder";

type TeeSheetInfoCardProps = {
  title: string;
  subTitle: string;
  onPress?: () => void;
  icon?: React.JSX.Element;
  bodyTitle?: string;
  bodyContent?: string;
  footer?: React.JSX.Element | null;
  backgroundColor?: keyof Theme["colors"];
  bodyColor?: keyof Theme["colors"];
  titleColor?: keyof Theme["colors"];
  courseNames?: string;
  showRotatingBorder?: boolean;
};

export const TeeSheetInfoCard = ({
  title,
  subTitle,
  onPress,
  icon,
  bodyTitle,
  bodyContent,
  footer,
  backgroundColor = "grayscale-200",
  bodyColor = "text-primary",
  titleColor = "text-primary",
  courseNames,
  showRotatingBorder = false,
}: TeeSheetInfoCardProps) => {
  const noLowerPart = !bodyTitle && !bodyContent && !footer;

  const getCourseText = () => {
    if (courseNames) {
      const splitCourseInfo = courseNames.split(", ");
      return courseNames === "All Courses"
        ? t`All Courses`
        : splitCourseInfo.length > 1
        ? `${splitCourseInfo.length} ${t`Courses`}`
        : splitCourseInfo[0];
    }
    return "";
  };

  const InfoCard = () => {
    return (
      <Pressable
        onPress={onPress}
        backgroundColor={backgroundColor}
        p={4}
        style={{
          borderRadius: 16,
          borderBottomLeftRadius: noLowerPart ? 0 : 16,
          borderBottomRightRadius: noLowerPart ? 0 : 16,
        }}
        width="100%"
        borderWidth={2}
        borderColor="transparent"
      >
        <HStack justifyContent="space-between">
          <HStack alignItems="center">
            {icon ? (
              <Box
                width={40}
                height={40}
                mr={4}
                backgroundColor="grayscale-0"
                borderRadius="2"
                justifyContent="center"
                alignItems="center"
                testID="tee-sheet-info-card-icon-container"
              >
                {icon}
              </Box>
            ) : null}
            <Box>
              {title ? (
                <Text
                  maxFontSizeMultiplier={2}
                  variant="mainSmallSemiBold"
                  color={titleColor}
                >
                  {title || ""}
                </Text>
              ) : null}
              {subTitle ? (
                <Text
                  maxFontSizeMultiplier={2}
                  variant="mainTiny"
                  color={bodyColor}
                >
                  {subTitle || ""}
                </Text>
              ) : null}
            </Box>
          </HStack>
          {courseNames ? (
            <HStack justifyContent="center" alignItems="center" gap={1}>
              <Text
                maxFontSizeMultiplier={2}
                variant="mainTiny"
                fontWeight={600}
              >
                {getCourseText()}
              </Text>
              <PlaceIcon color="black" fill="black" />
            </HStack>
          ) : null}
        </HStack>
        {bodyTitle || bodyContent ? (
          <Box mt={4}>
            {bodyTitle?.length ? (
              <Text
                maxFontSizeMultiplier={2}
                variant="mainBaseSemiBold"
                color={titleColor}
              >
                {bodyTitle || ""}
              </Text>
            ) : null}
            {bodyContent?.length ? (
              <Text
                maxFontSizeMultiplier={2}
                pt={bodyTitle?.length ? 2 : 0}
                variant="mainBaseMedium"
                color={bodyColor}
              >
                {bodyContent || ""}
              </Text>
            ) : null}
          </Box>
        ) : null}
        {!!footer ? <Box>{footer}</Box> : null}
      </Pressable>
    );
  };

  return showRotatingBorder ? (
    <RotatingGradientBorder color={backgroundColor}>
      <InfoCard />
    </RotatingGradientBorder>
  ) : (
    <InfoCard />
  );
};
